import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { TURN_STATUS, MAX_ATTACHMENTS, RUN_TERMINAL_STATUSES } from 'Pods/ThreeDStudio/constants'
import useImageAssetUpload from 'Pods/ThreeDStudio/Hooks/useImageAssetUpload'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import {
  closeImageAssistant,
  openImageEditor,
  setImageAssistantSessionId,
} from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import useSessionImageGenerations from './useSessionImageGenerations'
import { IMAGE_ACTION } from '../../imageModels'
import { getCurrentSessionId, isEditScreenRun, selectSessionId, subscribeToGeneratorRun } from '../../imageRun'
import { CREATE_TAGS } from '../../mockData'

// A restored run only keeps spinning while it is genuinely in flight; one that
// settled without an image (failed, canceled, timed out, rejected) shows the retry
// state instead of a spinner that can never resolve.
const turnStatusForRun = (status) => {
  if (status === 'succeeded') {
    return TURN_STATUS.DONE
  }

  return RUN_TERMINAL_STATUSES.has(status) ? TURN_STATUS.FAILED : TURN_STATUS.LOADING
}

// Each image action caps input_image differently, so the reference-image count decides
// which action the prompt is sent as: none is text-to-image, a single reference is
// similar-image (max 1), and several are blended by multi-image-edit (2-8). The backend
// then ranks the providers that offer that action.
const imageActionForCount = (imageCount) => {
  if (imageCount === 0) {
    return IMAGE_ACTION.TEXT_TO_IMAGE
  }

  return imageCount === 1 ? IMAGE_ACTION.SIMILAR_IMAGE : IMAGE_ACTION.MULTI_IMAGE_EDIT
}

/**
 * Owns the Create tab's state: the composer (prompt + reference attachments) and
 * the conversation of generation turns, plus the handlers wiring those to the
 * generation backend and the store.
 */
export default function useCreateTab() {
  const [prompt, setPrompt] = useState('')
  const [attachments, setAttachments] = useState(EMPTY_ARRAY)
  const [messages, setMessages] = useState(EMPTY_ARRAY)

  const sessionId = useThreeDStudioStore(selectSessionId)
  const { generations: sessionGenerations } = useSessionImageGenerations(sessionId)
  // The session holds every image run, including the Edit screen's masked and
  // instruction edits; only the ones started from this composer belong in the
  // conversation.
  const generations = useMemo(
    () => sessionGenerations.filter(run => !isEditScreenRun(run)),
    [sessionGenerations],
  )

  const fileInputRef = useRef(null)
  const idRef = useRef(0)
  const isMountedRef = useRef(true)
  const subscriptionsRef = useRef(new Map())
  const hydratedRef = useRef(false)

  useEffect(() => () => {
    isMountedRef.current = false
    subscriptionsRef.current.forEach(subscription => subscription.unsubscribe())
    subscriptionsRef.current.clear()
  }, [])

  const { postData } = useApiPost()
  const uploadImageAsset = useImageAssetUpload()

  const nextId = () => {
    idRef.current += 1
    return idRef.current
  }

  // Reference-image attachments drive which quick-action tags are offered.
  const tags = useMemo(() => {
    if (attachments.length === 0) {
      return EMPTY_ARRAY
    }
    return attachments.length === 1 ? CREATE_TAGS.SINGLE : CREATE_TAGS.MULTI
  }, [attachments])

  const isGenerating = useMemo(
    () => messages.some(message => message.status === TURN_STATUS.LOADING),
    [messages],
  )

  const setTurn = useCallback((turnId, updates) => {
    if (!isMountedRef.current) {
      return
    }
    setMessages(prev => prev.map(message => (
      message.id === turnId ? { ...message, ...updates } : message
    )))
  }, [])

  const failTurn = useCallback((turnId) => {
    setTurn(turnId, { status: TURN_STATUS.FAILED, result: null })
  }, [setTurn])

  // Subscribe to the run's websocket channel and resolve the turn when the backend
  // broadcasts the terminal update with the generated image (or marks it failed).
  // Keyed by run id: restoring the conversation re-subscribes to in-flight runs, and a
  // run the live send is already watching must not get a second subscription.
  const subscribeToRun = useCallback((runId, turnId) => {
    const runKey = String(runId)
    if (subscriptionsRef.current.has(runKey)) {
      return
    }

    const subscription = subscribeToGeneratorRun(runId, {
      onImage: (asset) => {
        subscriptionsRef.current.delete(runKey)
        setTurn(turnId, {
          status: TURN_STATUS.DONE,
          result: { id: asset.assetId, url: asset.fileUrl, alt: 'Generated image' },
        })
      },
      onFail: () => {
        subscriptionsRef.current.delete(runKey)
        failTurn(turnId)
      },
    })

    if (subscription) {
      subscriptionsRef.current.set(runKey, subscription)
    } else {
      failTurn(turnId)
    }
  }, [failTurn, setTurn])

  // Restore the conversation from the session's saved image generations once, after a
  // reload: succeeded runs become finished turns; still-running ones become loading turns
  // that reconnect to their websocket. We never clobber turns already in progress.
  useEffect(() => {
    if (hydratedRef.current || generations.length === 0) {
      return
    }
    hydratedRef.current = true

    const restored = generations.map((run) => {
      const output = run.outputAssets?.[0]
      const isDone = run.status === 'succeeded'
      return {
        id: run.id,
        runId: run.id,
        prompt: run.prompt,
        images: (run.inputAssets || []).map(asset => ({ id: asset.assetId, url: asset.fileUrl })),
        inputAssetIds: (run.inputAssets || []).map(asset => asset.assetId),
        status: turnStatusForRun(run.status),
        result: isDone && output
          ? { id: output.assetId, url: output.fileUrl, alt: 'Generated image' }
          : null,
      }
    })
    idRef.current = restored.reduce((max, turn) => Math.max(max, Number(turn.id) || 0), idRef.current)
    setMessages(prev => (prev.length > 0 ? prev : restored))

    generations.forEach((run) => {
      if (!RUN_TERMINAL_STATUSES.has(run.status)) {
        subscribeToRun(run.id, run.id)
      }
    })
  }, [generations, subscribeToRun])

  // Create an image run, attaching it to the existing session when one exists, then wait
  // for the websocket terminal update to surface the image.
  //
  // No model_id is sent: that would lock the run to one provider and make it ineligible
  // for failover. The action rides on use_case instead, which is what
  // CreateFromProvider#infer_action reads for an image_generation carrying input_image, so
  // the backend ranks flux against nano-banana itself.
  const runGeneration = useCallback((turnId, turnPrompt, inputAssetIds) => {
    const activeSessionId = getCurrentSessionId()
    const imageCount = Array.isArray(inputAssetIds) ? inputAssetIds.length : 0
    const canonicalParams = imageCount > 0
      ? { prompt: turnPrompt, input_image: inputAssetIds.map(assetId => ({ asset_id: assetId })) }
      : { prompt: turnPrompt }

    postData({
      path: '/api/internal/generator/runs',
      data: {
        data: {
          run_type: 'image_generation',
          use_case: imageActionForCount(imageCount),
          ...(activeSessionId ? { generator_session_id: activeSessionId } : {}),
          canonical_params: canonicalParams,
        },
      },
      onSuccess: (response) => {
        const { id, generatorSessionId } = response.data.data.attributes
        if (generatorSessionId) {
          setImageAssistantSessionId(generatorSessionId)
        }
        setTurn(turnId, { runId: id })
        subscribeToRun(id, turnId)
      },
      onError: () => failTurn(turnId),
    })
  }, [postData, subscribeToRun, failTurn, setTurn])

  // Upload any reference images to reusable Generator::Assets, then start the run.
  // Already-uploaded ids (regenerate/retry) are reused so we never re-upload.
  const startGeneration = useCallback((turnId, turnPrompt, turnImages, existingAssetIds) => {
    if (existingAssetIds && existingAssetIds.length > 0) {
      runGeneration(turnId, turnPrompt, existingAssetIds)
      return
    }
    if (!turnImages || turnImages.length === 0) {
      runGeneration(turnId, turnPrompt, EMPTY_ARRAY)
      return
    }

    const activeSessionId = getCurrentSessionId()
    Promise.all(turnImages.map(image => uploadImageAsset(image.file, { sessionId: activeSessionId })))
      .then((assetIds) => {
        if (!isMountedRef.current) {
          return
        }
        setTurn(turnId, { inputAssetIds: assetIds })
        runGeneration(turnId, turnPrompt, assetIds)
      })
      .catch(() => failTurn(turnId))
  }, [uploadImageAsset, runGeneration, setTurn, failTurn])

  const handlePromptChange = useCallback(event => setPrompt(event.target.value), [])

  const handleTagClick = useCallback(tag => setPrompt(tag), [])

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find(image => image.id === id)
      if (removed) {
        URL.revokeObjectURL(removed.url)
      }
      return prev.filter(image => image.id !== id)
    })
  }, [])

  const handleFilesChange = useCallback((event) => {
    const picked = Array.from(event.target.files || [])
    const remaining = MAX_ATTACHMENTS - attachments.length
    if (picked.length && remaining > 0) {
      const added = picked
        .slice(0, remaining)
        .map(file => ({ id: nextId(), url: URL.createObjectURL(file), file }))
      setAttachments(prev => [...prev, ...added])
    }
    // Reset so picking the same file again still fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachments])

  const handleSend = useCallback(() => {
    const trimmed = prompt.trim()
    if (!trimmed) {
      return
    }

    const turnId = nextId()
    const turnImages = attachments
    setMessages(prev => [
      ...prev,
      { id: turnId, prompt: trimmed, images: turnImages, status: TURN_STATUS.LOADING, result: null },
    ])
    setPrompt('')
    setAttachments(EMPTY_ARRAY)
    startGeneration(turnId, trimmed, turnImages, null)
  }, [prompt, attachments, startGeneration])

  // Regenerate re-sends the same request and appends only the new image — the
  // sent prompt/images are not shown again (continuation turn).
  const handleRegenerate = useCallback((turn) => {
    if (!turn.runId) {
      return
    }
    const turnId = nextId()
    setMessages(prev => [
      ...prev,
      {
        id: turnId,
        prompt: turn.prompt,
        images: turn.images,
        status: TURN_STATUS.LOADING,
        result: null,
        continuation: true,
      },
    ])

    postData({
      path: `/api/internal/generator/image-generations/${turn.runId}/regenerate`,
      data: {},
      onSuccess: (response) => {
        const { id, generatorSessionId } = response.data.data.attributes
        if (generatorSessionId) {
          setImageAssistantSessionId(generatorSessionId)
        }
        setTurn(turnId, { runId: id })
        subscribeToRun(id, turnId)
      },
      onError: () => failTurn(turnId),
    })
  }, [postData, setTurn, failTurn, subscribeToRun])

  // Retry a failed turn in place: flip it back to loading and re-run.
  const handleGenerateAgain = useCallback((turn) => {
    setTurn(turn.id, { status: TURN_STATUS.LOADING, result: null })
    startGeneration(turn.id, turn.prompt, turn.images, turn.inputAssetIds)
  }, [startGeneration, setTurn])

  // The image is already a Generator::Asset (ai_generated output), so reuse its id as
  // the 3D input instead of re-uploading the file.
  const handleApply = useCallback((image) => {
    setInputImage(image.id, image.url)
    closeImageAssistant()
  }, [])

  const handleEdit = useCallback((image) => {
    openImageEditor(image, IMAGE_ASSISTANT_TAB.CREATE)
  }, [])

  return {
    prompt,
    attachments,
    messages,
    tags,
    isGenerating,
    isUploadDisabled: attachments.length >= MAX_ATTACHMENTS,
    fileInputRef,
    handlePromptChange,
    handleTagClick,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleRegenerate,
    handleGenerateAgain,
    handleApply,
    handleEdit,
  }
}
